import React, { useEffect, useState, useCallback, useRef } from 'react';
import Pagination from '../../components/Pagination/Pagination';
import axios from 'axios';
import { getAuthHeaders } from '../../utils/auth';
import { formatUsDateFromIso, todayIsoLocal } from '../../utils/usDateFormat';
import Loader from '../../components/Loader/Loader';
import AttendancePopup from '../../components/Modals/AttendancePopup';
import AttendanceDateRangeFilter from '../../components/AttendanceDateRangeFilter/AttendanceDateRangeFilter';
import AttendanceDaysFilter from '../../components/AttendanceDaysFilter/AttendanceDaysFilter';
import SelfieCapture from '../../components/SelfieCapture/SelfieCapture';
import {
  defaultAttendanceDateRange,
  defaultCustomAttendanceRange,
  buildAttendanceHistoryDateParams,
  canFetchWithAttendanceDateFilter,
  DEFAULT_ATTENDANCE_DAYS,
  ATTENDANCE_DAYS_FILTER_CUSTOM,
  formatAttendanceHistorySummary,
} from '../../utils/attendanceDateRange';
import {
  formatAttendanceMinutes,
  formatCheckInOutDurationFromRecord,
  formatShiftPolicyMinutes,
  needsEarlyCheckoutAcknowledgment as computeNeedsEarlyCheckoutAcknowledgment,
} from '../../utils/attendanceMetrics';
import AttendanceShiftName from '../../components/AttendanceShiftName/AttendanceShiftName';

const LOCATION_RADIUS_METERS = 500;

export default function CheckInCheckOut({ userProfile }) {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [status, setStatus] = useState(null);
  const [checkInInfo, setCheckInInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(100);
  const defaultRange = defaultAttendanceDateRange();
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [attendanceDays, setAttendanceDays] = useState(DEFAULT_ATTENDANCE_DAYS);
  const [attendanceFilterMode, setAttendanceFilterMode] = useState('days');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [popupTitle, setPopupTitle] = useState('');
  const [popupSecondaryButtonText, setPopupSecondaryButtonText] = useState('');
  const [popupOnSecondaryClick, setPopupOnSecondaryClick] = useState(null);

  const [showSelfieCapture, setShowSelfieCapture] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isCheckInProcessing, setIsCheckInProcessing] = useState(false);
  const [isCheckOutProcessing, setIsCheckOutProcessing] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [userCoordinates, setUserCoordinates] = useState(null);
  const [locationPermissionGranted, setLocationPermissionGranted] = useState(false);
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);
  const [permissionState, setPermissionState] = useState('unknown');
  const [locationAutoMatchFailed, setLocationAutoMatchFailed] = useState(false);
  const [noLocationReason, setNoLocationReason] = useState('');
  const [noLocationInformManager, setNoLocationInformManager] = useState(false);
  const [noLocationSelectedLocation, setNoLocationSelectedLocation] = useState('');
  const [manualLatitude, setManualLatitude] = useState('');
  const [manualLongitude, setManualLongitude] = useState('');
  const [currentAction, setCurrentAction] = useState(null); // 'checkIn' or 'checkOut'
  const [hasManuallyUnselected, setHasManuallyUnselected] = useState(false);
  const [showNearbyLocationsModal, setShowNearbyLocationsModal] = useState(false);
  const [nearbyLocations, setNearbyLocations] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  // After a successful punch, keep dropdown cleared until the next Check In/Out click.
  const pauseAutoDetectRef = useRef(false);

  const getStatusLabel = (statusValue) => {
    if (statusValue === 'checked_in') return 'Checked In';
    if (statusValue === 'absent' || statusValue === 'leave') return 'Absent';
    return 'Checked Out';
  };
  const getStatusClass = (statusValue) => {
    if (statusValue === 'checked_in') return 'bg-green-100 text-green-700';
    if (statusValue === 'absent' || statusValue === 'leave') return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  const showPopup = (title, message, secondaryButtonText = '', onSecondaryClick = null) => {
    setPopupTitle(title);
    setPopupMessage(message);
    setPopupSecondaryButtonText(secondaryButtonText || '');
    setPopupOnSecondaryClick(onSecondaryClick ? () => onSecondaryClick : null);
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setPopupTitle('');
    setPopupMessage('');
    setPopupSecondaryButtonText('');
    setPopupOnSecondaryClick(null);
  };

  const isManualAttendanceFlow = () =>
    !locationPermissionGranted ||
    !userCoordinates ||
    locationAutoMatchFailed ||
    !selectedLocation;

  const needsEarlyCheckoutAcknowledgment = () =>
    computeNeedsEarlyCheckoutAcknowledgment(checkInInfo);

  const validateManualOutsideFields = (actionLabel) => {
    const reason = noLocationReason.trim();
    if (reason.length < 3) {
      showPopup(
        'Required fields',
        `Please enter a reason for ${actionLabel} outside your assigned location.`
      );
      return false;
    }
    if (!noLocationInformManager) {
      showPopup(
        'Required fields',
        'Please confirm that you want to inform your manager.'
      );
      return false;
    }
    return true;
  };

  const validateEarlyCheckOutFields = () => {
    const reason = noLocationReason.trim();
    if (reason.length < 3) {
      showPopup(
        'Required fields',
        'Please enter a reason for checking out before the required work duration.'
      );
      return false;
    }
    if (!noLocationInformManager) {
      showPopup(
        'Required fields',
        'Please confirm that you want to inform your manager.'
      );
      return false;
    }
    return true;
  };

  const validateManualCheckInFields = () => validateManualOutsideFields('checking in');

  const validateManualCheckOutFields = () => validateManualOutsideFields('checking out');

  const refreshUserCoordinates = useCallback((options = {}) => {
    const { showErrorPopup = false } = options;
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        if (showErrorPopup) {
          showPopup('Error', 'Geolocation is not supported by your browser.');
        }
        setLocationPermissionDenied(true);
        resolve(null);
        return;
      }

      setIsDetectingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coordinates = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          setUserCoordinates(coordinates);
          setLocationPermissionGranted(true);
          setLocationPermissionDenied(false);
          setPermissionState('granted');
          setIsDetectingLocation(false);
          resolve(coordinates);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setIsDetectingLocation(false);
          if (showErrorPopup) {
            setLocationPermissionDenied(true);
            setLocationPermissionGranted(false);
            setPermissionState('denied');
            let errorMessage = 'Unable to access your location. ';
            switch (error.code) {
              case error.PERMISSION_DENIED:
                errorMessage += 'Please allow location access to use this feature.';
                break;
              case error.POSITION_UNAVAILABLE:
                errorMessage += 'Location information is unavailable.';
                break;
              case error.TIMEOUT:
                errorMessage += 'Location request timed out.';
                break;
              default:
                errorMessage += 'An unknown error occurred.';
                break;
            }
            showPopup('Location Access Required', errorMessage);
          }
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  }, []);

  const checkPermissionStatus = async () => {
    try {
      if (!navigator?.permissions || !navigator?.permissions?.query) return;
      const result = await navigator.permissions.query({ name: 'geolocation' });
      setPermissionState(result.state);
      if (result.onchange == null) return;
      result.onchange = () => {
        setPermissionState(result.state);
        if (result.state === 'granted') {
          refreshUserCoordinates();
        }
      };
    } catch (_) {
      // Permissions API not supported; ignore
    }
  };

  const requestLocationPermission = () => {
    refreshUserCoordinates({ showErrorPopup: true });
  };

  const resetLocationSelection = () => {
    // Keep cleared until the user starts the next check-in/out (don't immediately re-select).
    pauseAutoDetectRef.current = true;
    setSelectedLocation('');
    setHasManuallyUnselected(false);
    setLocationAutoMatchFailed(false);
    setShowNearbyLocationsModal(false);
    setNearbyLocations([]);
  };

  const handleSelfieCapture = useCallback(async (imageSrc) => {
    
    setShowSelfieCapture(false);
    setIsProcessing(true);

    const isManual =
      !locationPermissionGranted ||
      !userCoordinates ||
      locationAutoMatchFailed ||
      !selectedLocation;
    const requiresEarlyAck =
      currentAction === 'checkOut' &&
      !isManual &&
      computeNeedsEarlyCheckoutAcknowledgment(checkInInfo);
    
    // Capture current state values
    const extraFieldsData = {
      noLocationSelectedLocation,
      noLocationReason,
      noLocationInformManager,
      manualLatitude,
      manualLongitude,
      requiresEarlyAck,
    };
    
    try {
      if (currentAction === 'checkIn') {
        // Use dropdown location if available, otherwise use manual entry
        const effectiveLocation = selectedLocation || noLocationSelectedLocation;
        await processCheckInWithSelfie(imageSrc, effectiveLocation, locations, extraFieldsData);
      } else {
        // Use dropdown location if available, otherwise use manual entry
        const effectiveLocation = selectedLocation || noLocationSelectedLocation;
        await processCheckOutWithSelfie(imageSrc, effectiveLocation, locations, extraFieldsData);
      }
    } catch (error) {
      console.error('Error processing selfie:', error);
      showPopup('Error', 'Failed to process selfie. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [currentAction, selectedLocation, locations, noLocationSelectedLocation, noLocationReason, noLocationInformManager, manualLatitude, manualLongitude, checkInInfo, locationPermissionGranted, userCoordinates, locationAutoMatchFailed]);

  const startSelfieCapture = () => {
    setShowSelfieCapture(true);
  };

  const closeSelfieCapture = () => {
    setShowSelfieCapture(false);
  };

  const openImageModal = (record) => {
    setSelectedRecord(record);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedRecord(null);
  };

  const clearFormFields = () => {
    setNoLocationReason('');
    setNoLocationInformManager(false);
    setNoLocationSelectedLocation('');
    // Don't clear manual coordinates as they might be needed for checkout
    // setManualLatitude('');
    // setManualLongitude('');
    setCurrentAction(null);
  };

  const fetchCheckInStatus = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/attendance/status`,
        { headers }
      );

      if (response.data.is_checked_in) {
        setStatus('checkedIn');
        setCheckInInfo({
          location: response.data.location_name,
          location_id: response.data.location_id,
          time: response.data.check_in_time_str,
          date: response.data.check_in_date,
          checkInTimeUtc: response.data.check_in_time_utc || null,
          checkOutTime: null,
          coordinates: {
            lat: response.data.check_in_latitude,
            lng: response.data.check_in_longitude,
          },
          durationText: response.data.is_stale_open_session
            ? '-'
            : response.data.duration_text,
          isStaleOpenSession: response.data.is_stale_open_session === true,
          lateCheckinMinutes: response.data.late_checkin_minutes ?? 0,
          earlyLeaveMinutes: response.data.early_leave_minutes ?? 0,
          checkinWindowViolationMinutes: response.data.checkin_window_violation_minutes ?? 0,
          overtimeMinutes: response.data.overtime_minutes ?? 0,
          isSpecialShift: response.data.is_special_shift === true,
          minWorkMinutes: response.data.min_work_minutes ?? null,
          scheduledMinutes: response.data.scheduled_minutes ?? null,
          priorDayWorkedMinutes: response.data.prior_day_worked_minutes ?? 0,
          isEarlyCheckout: response.data.is_early_checkout === true,
        });
        // Do not lock dropdown to check-in branch; auto-detect uses current GPS for checkout.
      }
    } catch (error) {
      console.error('Error fetching attendance status:', error);
      showPopup('Error', 'Failed to fetch check-in status. Please try again later.');
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/system/locations`,
        {
          headers: {
            accept: 'application/json',
          },
        }
      );

      const fetchedLocations = response.data.locations || [];
      setLocations(fetchedLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
      showPopup('Error', 'Failed to fetch locations. Please try again later.');
    }
  };

  const fetchHistory = async (page = 1) => {
    const rangeStart =
      attendanceFilterMode === ATTENDANCE_DAYS_FILTER_CUSTOM ? startDate : '';
    const rangeEnd =
      attendanceFilterMode === ATTENDANCE_DAYS_FILTER_CUSTOM ? endDate : '';
    if (
      attendanceFilterMode === ATTENDANCE_DAYS_FILTER_CUSTOM &&
      !canFetchWithAttendanceDateFilter(rangeStart, rangeEnd)
    ) {
      return;
    }
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/attendance/history`,
        {
          headers,
          params: {
            ...buildAttendanceHistoryDateParams(rangeStart, rangeEnd, attendanceDays),
            page,
            page_size: pageSize,
          },
        }
      );

      if (response.data.success && Array.isArray(response.data.records)) {
        setHistory(response.data.records);
        setTotalRecords(response.data.total);
        setTotalPages(response.data.total_pages);
        setCurrentPage(response.data.page);
      }
    } catch (error) {
      console.error('Error fetching attendance history:', error);
      showPopup('Error', 'Failed to fetch attendance history. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkPermissionStatus();
    fetchCheckInStatus();
    fetchLocations();
    requestLocationPermission();
  }, []);

  useEffect(() => {
    fetchHistory(currentPage);
  }, [currentPage, startDate, endDate, attendanceDays, attendanceFilterMode]);

  const handleAttendanceFilterModeChange = (mode) => {
    setAttendanceFilterMode(mode);
    setCurrentPage(1);
    if (mode === ATTENDANCE_DAYS_FILTER_CUSTOM) {
      const customRange = defaultCustomAttendanceRange();
      setStartDate(customRange.startDate);
      setEndDate(customRange.endDate);
    } else {
      setStartDate('');
      setEndDate('');
    }
  };

  const handleAttendanceStartChange = (iso) => {
    setStartDate(iso);
    setCurrentPage(1);
  };

  const handleAttendanceEndChange = (iso) => {
    setEndDate(iso);
    setCurrentPage(1);
  };

  const parseExportError = async (err) => {
    const data = err.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text);
        if (typeof parsed.detail === 'string') return parsed.detail;
      } catch (_) {
        /* ignore */
      }
    }
    if (typeof data?.detail === 'string') return data.detail;
    return 'Failed to export attendance. Please try again.';
  };

  const handleExport = async () => {
    const rangeStart =
      attendanceFilterMode === ATTENDANCE_DAYS_FILTER_CUSTOM ? startDate : '';
    const rangeEnd =
      attendanceFilterMode === ATTENDANCE_DAYS_FILTER_CUSTOM ? endDate : '';
    if (
      attendanceFilterMode === ATTENDANCE_DAYS_FILTER_CUSTOM &&
      !canFetchWithAttendanceDateFilter(rangeStart, rangeEnd)
    ) {
      return;
    }
    try {
      setExporting(true);
      setExportError(null);
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/attendance/history/export`,
        {
          headers,
          params: buildAttendanceHistoryDateParams(rangeStart, rangeEnd, attendanceDays),
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], {
        type:
          response.headers['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";\n]+)"?/i);
      link.download = match ? match[1].trim() : 'my_attendance_export.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting attendance:', err);
      setExportError(await parseExportError(err));
    } finally {
      setExporting(false);
    }
  };

  // Auto-detect location when locations are loaded and user coordinates are available
  // (including while checked in, so checkout can target the current nearby site).
  // Skipped after a successful punch until the next Check In/Out click.
  useEffect(() => {
    if (pauseAutoDetectRef.current) return;
    if (locations.length > 0 && !selectedLocation && userCoordinates && locationPermissionGranted && !hasManuallyUnselected) {
      autoSelectLocationBasedOnUserPosition();
    }
  }, [locations, selectedLocation, status, userCoordinates, locationPermissionGranted, hasManuallyUnselected]);

  // If user granted permission but coordinates are still unavailable, likely system-level location is disabled
  useEffect(() => {
    if (locationPermissionGranted && !userCoordinates) {
      const timer = setTimeout(() => {
        if (!userCoordinates) {
          showPopup(
            'Location disabled in System',
            'You allowed location access in the browser, but your device\'s Location Services seem disabled or blocked. Please enable Location Services in your system settings and reload the page.'
          );
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [locationPermissionGranted, userCoordinates]);

  // Handle Escape key for nearby locations modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showNearbyLocationsModal) {
        setShowNearbyLocationsModal(false);
      }
    };
    
    if (showNearbyLocationsModal) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [showNearbyLocationsModal]);

  const isWithinRadius = (lat1, lon1, lat2, lon2, radiusInMeters = 10) => {
    if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) {
      console.error('Invalid coordinates passed to isWithinRadius:', {
        lat1,
        lon1,
        lat2,
        lon2,
      });
      return false;
    }

    const R = 6371000;
    const toRad = (deg) => deg * (Math.PI / 180);
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance <= radiusInMeters;
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) {
      return Infinity;
    }

    const R = 6371000; // Earth's radius in meters
    const toRad = (deg) => deg * (Math.PI / 180);
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const findClosestLocation = (userLat, userLng, locations) => {
    if (!locations || locations.length === 0) {
      return null;
    }

    let closestLocation = null;
    let minDistance = Infinity;

    locations.forEach((location) => {
      if (location.latitude != null && location.longitude != null) {
        const distance = calculateDistance(
          userLat,
          userLng,
          location.latitude,
          location.longitude
        );
        
        if (distance < minDistance) {
          minDistance = distance;
          closestLocation = location;
        }
      }
    });

    // Only return the closest location if it's within configured radius.
    return minDistance <= LOCATION_RADIUS_METERS ? closestLocation : null;
  };

  const findNearbyLocations = (userLat, userLng, locations) => {
    if (!locations || locations.length === 0) {
      return [];
    }

    const nearby = [];

    locations.forEach((location) => {
      if (location.latitude != null && location.longitude != null) {
        const distance = calculateDistance(
          userLat,
          userLng,
          location.latitude,
          location.longitude
        );
        
        if (distance <= LOCATION_RADIUS_METERS) {
          nearby.push({
            ...location,
            distance: Math.round(distance) // distance in meters
          });
        }
      }
    });

    // Sort by distance (closest first)
    return nearby.sort((a, b) => a.distance - b.distance);
  };

  const autoSelectLocationBasedOnUserPosition = (coordsOverride = null) => {
    const coords = coordsOverride || userCoordinates;
    if (!coords || !locations || locations.length === 0) {
      console.log('Cannot auto-select location: missing user coordinates or locations');
      return { nearbyCount: 0, selectedName: '' };
    }

    const nearby = findNearbyLocations(
      coords.latitude,
      coords.longitude,
      locations
    );

    if (nearby.length === 0) {
      console.log(`No location found within ${LOCATION_RADIUS_METERS}m radius. User needs to select manually.`);
      setLocationAutoMatchFailed(true);
      showPopup(
        'No nearby location found',
        `We could not detect a site within ${LOCATION_RADIUS_METERS} meters of your current position. Please select your location from the dropdown to continue.`
      );
      return { nearbyCount: 0, selectedName: '' };
    }

    if (nearby.length === 1) {
      setSelectedLocation(nearby[0].name);
      setLocationAutoMatchFailed(false);
      setShowNearbyLocationsModal(false);
      return { nearbyCount: 1, selectedName: nearby[0].name };
    }

    // Multiple nearby locations - show modal for user to choose
    setNearbyLocations(nearby);
    setShowNearbyLocationsModal(true);
    setSelectedLocation(nearby[0].name);
    setLocationAutoMatchFailed(false);
    return { nearbyCount: nearby.length, selectedName: nearby[0].name };
  };

  const handleCheckIn = async () => {
    if (!navigator.geolocation) {
      showPopup('Error', 'Geolocation is not supported by your browser.');
      return;
    }

    setCurrentAction('checkIn');
    pauseAutoDetectRef.current = false;
    const coords = await refreshUserCoordinates();
    const activeCoords = coords || userCoordinates;
    const hasCoords = Boolean(activeCoords);
    const permissionOk = locationPermissionGranted || Boolean(coords);

    let nextSelected = selectedLocation;
    if (!nextSelected && hasCoords && locations.length > 0) {
      const result = autoSelectLocationBasedOnUserPosition(activeCoords);
      nextSelected = result.selectedName;
      // Multiple nearby: let the user confirm in the modal, then press Check In again.
      if (result.nearbyCount > 1) {
        return;
      }
    }

    // Show selfie modal with extra fields if:
    // 1. Location permission not granted, OR
    // 2. Location permission granted but no coordinates available, OR  
    // 3. Auto-match failed (no location within configured radius), OR
    // 4. No location selected from dropdown
    if (!permissionOk || !hasCoords || locationAutoMatchFailed || !nextSelected) {
      setShowSelfieCapture(true);
      return;
    }

    setIsCheckInProcessing(true);
    startSelfieCapture();
  };

  const base64ToFile = (base64String, filename = 'selfie.jpg') => {
    const base64Data = base64String.replace(/^data:image\/[a-z]+;base64,/, '');
    
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    
    return new File([byteArray], filename, { type: 'image/jpeg' });
  };

  const processCheckInWithSelfie = async (selfieBase64, currentSelectedLocation, currentLocations, extraFieldsData) => {
    try {
      if (isManualAttendanceFlow()) {
        const reason = (extraFieldsData?.noLocationReason || '').trim();
        if (reason.length < 3) {
          showPopup(
            'Required fields',
            'Please enter a reason for checking in outside your assigned location.'
          );
          setIsCheckInProcessing(false);
          return;
        }
        if (!extraFieldsData?.noLocationInformManager) {
          showPopup(
            'Required fields',
            'Please confirm that you want to inform your manager.'
          );
          setIsCheckInProcessing(false);
          return;
        }
      }

      // Use stored user coordinates or manual input
      const userLat = userCoordinates ? userCoordinates.latitude : (extraFieldsData?.manualLatitude ? parseFloat(extraFieldsData.manualLatitude) : 0);
      const userLng = userCoordinates ? userCoordinates.longitude : (extraFieldsData?.manualLongitude ? parseFloat(extraFieldsData.manualLongitude) : 0);

      const effectiveSelectedLocation = currentSelectedLocation || extraFieldsData?.noLocationSelectedLocation || '';
      const selectedLoc = currentLocations.find((loc) => loc.name === effectiveSelectedLocation);
      
      // Skip radius when using Inform Manager / outside-location path
      const skipRadiusForManual =
        isManualAttendanceFlow() || Boolean(extraFieldsData?.noLocationInformManager);

      // Check if user is within configured radius of selected location
      if (selectedLoc && !skipRadiusForManual) {
        const isNear = isWithinRadius(
          userLat,
          userLng,
          selectedLoc.latitude,
          selectedLoc.longitude,
          LOCATION_RADIUS_METERS
        );
        if (!isNear) {
          showPopup(
            'Warning',
            'You are too far from the selected location. Move closer to the selected location.',
            'Check in manually',
            () => {
              setSelectedLocation('');
              setHasManuallyUnselected(true);
              setLocationAutoMatchFailed(true);
              setShowSelfieCapture(true);
              setCurrentAction('checkIn');
              setIsCheckInProcessing(false);
            }
          );
          setIsCheckInProcessing(false);
          return;
        }
      }
      
      const headers = await getAuthHeaders();
      const now = new Date();
      const formattedDate = todayIsoLocal();
      const formattedTime = now.toTimeString().split(' ')[0].slice(0, 5);
      const timezoneOffsetMinutes = new Date().getTimezoneOffset() * -1;

      try {
        const imageFile = base64ToFile(selfieBase64, 'checkin_selfie.jpg');
        
        const formData = new FormData();
        formData.append('date', formattedDate);
        formData.append('time', formattedTime);
        if (selectedLoc?.id) {
          formData.append('location_id', selectedLoc.id);
        }
        formData.append('latitude', userLat.toString());
        formData.append('longitude', userLng.toString());
        formData.append('timezone_offset_minutes', timezoneOffsetMinutes.toString());
        formData.append('image', imageFile);
        
        // Always send additional fields when extra content is shown
        formData.append('no_location_reason', extraFieldsData?.noLocationReason || '');
        formData.append('inform_manager', String(extraFieldsData?.noLocationInformManager || false));
        formData.append('selected_location_name', extraFieldsData?.noLocationSelectedLocation || effectiveSelectedLocation || '');

        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/attendance/checkin`,
          formData,
          { 
            headers: {
              ...headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        setStatus('checkedIn');
        const { location_name, check_in_time_str, check_in_date, check_in_lat, check_in_lon, location_id } =
          response.data.data;

        setCheckInInfo({
          location: location_name,
          location_id: location_id,
          time: check_in_time_str,
          date: check_in_date,
          checkInTimeUtc: response.data.data.check_in_time_utc || new Date().toISOString(),
          checkOutTime: null,
          coordinates: {
            lat: check_in_lat,
            lng: check_in_lon,
          },
          lateCheckinMinutes: response.data.data.late_checkin_minutes ?? 0,
          earlyLeaveMinutes: response.data.data.early_leave_minutes ?? 0,
          checkinWindowViolationMinutes:
            response.data.data.checkin_window_violation_minutes ?? 0,
          overtimeMinutes: response.data.data.overtime_minutes ?? 0,
          isSpecialShift: response.data.data.is_special_shift === true,
          minWorkMinutes: response.data.data.min_work_minutes ?? null,
          scheduledMinutes: response.data.data.scheduled_minutes ?? null,
          priorDayWorkedMinutes: response.data.data.prior_day_worked_minutes ?? 0,
          isEarlyCheckout: response.data.data.is_early_checkout === true,
        });
        resetLocationSelection();
        fetchHistory(currentPage);
        setIsCheckInProcessing(false);
        showPopup('Success', 'Successfully checked in!');
        clearFormFields();
      } catch (error) {
        setIsCheckInProcessing(false);
        if (error.response?.data?.detail) {
          showPopup('Error', `Failed to check in: ${error.response.data.detail}`);
        } else {
          showPopup('Error', 'Failed to check in. Please try again later.');
        }
      }
    } catch (error) {
      console.error('Error processing check-in:', error);
      showPopup('Error', 'Error processing check-in. Please try again.');
      setIsCheckInProcessing(false);
    }
  };

  const handleCheckOut = async () => {
    if (!navigator.geolocation) {
      showPopup('Error', 'Geolocation is not supported by your browser.');
      return;
    }

    setCurrentAction('checkOut');
    pauseAutoDetectRef.current = false;
    const coords = await refreshUserCoordinates();
    const activeCoords = coords || userCoordinates;
    const hasCoords = Boolean(activeCoords);
    const permissionOk = locationPermissionGranted || Boolean(coords);

    let nextSelected = selectedLocation;
    if (!nextSelected && hasCoords && locations.length > 0) {
      const result = autoSelectLocationBasedOnUserPosition(activeCoords);
      nextSelected = result.selectedName;
      // Multiple nearby: let the user confirm in the modal, then press Check Out again.
      if (result.nearbyCount > 1) {
        return;
      }
    }

    // Show selfie modal with extra fields if:
    // 1. Location permission not granted, OR
    // 2. Location permission granted but no coordinates available, OR  
    // 3. Auto-match failed (no location within configured radius), OR
    // 4. No location selected from dropdown
    if (!permissionOk || !hasCoords || locationAutoMatchFailed || !nextSelected) {
      setShowSelfieCapture(true);
      return;
    }

    setIsCheckOutProcessing(true);
    startSelfieCapture();
  };

  const processCheckOutWithSelfie = async (selfieBase64, currentSelectedLocation, currentLocations, extraFieldsData) => {
    try {
      const requiresEarlyAck = Boolean(extraFieldsData?.requiresEarlyAck);
      if (isManualAttendanceFlow()) {
        const reason = (extraFieldsData?.noLocationReason || '').trim();
        if (reason.length < 3) {
          showPopup(
            'Required fields',
            'Please enter a reason for checking out outside your assigned location.'
          );
          setIsCheckOutProcessing(false);
          return;
        }
        if (!extraFieldsData?.noLocationInformManager) {
          showPopup(
            'Required fields',
            'Please confirm that you want to inform your manager.'
          );
          setIsCheckOutProcessing(false);
          return;
        }
      } else if (requiresEarlyAck) {
        const reason = (extraFieldsData?.noLocationReason || '').trim();
        if (reason.length < 3) {
          showPopup(
            'Required fields',
            'Please enter a reason for checking out before the required work duration.'
          );
          setIsCheckOutProcessing(false);
          return;
        }
        if (!extraFieldsData?.noLocationInformManager) {
          showPopup(
            'Required fields',
            'Please confirm that you want to inform your manager.'
          );
          setIsCheckOutProcessing(false);
          return;
        }
      }

      // Prefer fresh GPS; fall back to manual input only (do not reuse check-in coords)
      const userLat = userCoordinates
        ? userCoordinates.latitude
        : (extraFieldsData?.manualLatitude ? parseFloat(extraFieldsData.manualLatitude) : 0);
      const userLng = userCoordinates
        ? userCoordinates.longitude
        : (extraFieldsData?.manualLongitude ? parseFloat(extraFieldsData.manualLongitude) : 0);

      const effectiveSelectedLocation = currentSelectedLocation || extraFieldsData?.noLocationSelectedLocation || '';
      const selectedLoc = currentLocations.find((loc) => loc.name === effectiveSelectedLocation);

      // Skip client radius only for outside/manual path (not early on-site Inform Manager).
      const skipRadiusForManual = isManualAttendanceFlow();

      // Only check radius for normal on-site checkout
      if (userCoordinates && locationPermissionGranted && selectedLoc && !skipRadiusForManual) {
        const isNear = isWithinRadius(
          userLat,
          userLng,
          selectedLoc.latitude,
          selectedLoc.longitude,
          LOCATION_RADIUS_METERS
        );
        if (!isNear) {
          showPopup(
            'Warning',
            'You are too far from the selected location. Move closer to check out, or check out manually.',
            'Check out manually',
            () => {
              setSelectedLocation('');
              setHasManuallyUnselected(true);
              setLocationAutoMatchFailed(true);
              setShowSelfieCapture(true);
              setCurrentAction('checkOut');
              setIsCheckOutProcessing(false);
            }
          );
          setIsCheckOutProcessing(false);
          return;
        }
      }

      try {
        const headers = await getAuthHeaders();
        const now = new Date();
        const formattedDate = todayIsoLocal();
        const formattedTime = now.toTimeString().split(' ')[0].slice(0, 5);
        const timezoneOffsetMinutes = new Date().getTimezoneOffset() * -1;

        const imageFile = base64ToFile(selfieBase64, 'checkout_selfie.jpg');

        const formData = new FormData();
        formData.append('date', formattedDate);
        formData.append('time', formattedTime);
        if (selectedLoc?.id) {
          formData.append('location_id', selectedLoc.id);
        }
        formData.append('latitude', userLat.toString());
        formData.append('longitude', userLng.toString());
        formData.append('timezone_offset_minutes', timezoneOffsetMinutes.toString());
        formData.append('notes', ''); // Empty notes for now
        formData.append('image', imageFile);
        
        // Always send additional fields when extra content is shown
        formData.append('no_location_reason', extraFieldsData?.noLocationReason || '');
        formData.append('inform_manager', String(extraFieldsData?.noLocationInformManager || false));
        formData.append('selected_location_name', extraFieldsData?.noLocationSelectedLocation || effectiveSelectedLocation || '');

        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/attendance/checkout`,
          formData,
          { 
            headers: {
              ...headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        const { check_out_time_str, check_out_lat, check_out_lon } = response.data.data;

        setStatus('checkedOut');
        setCheckInInfo((prev) => ({
          ...prev,
          checkOutTime: check_out_time_str,
          coordinates: {
            lat: check_out_lat,
            lng: check_out_lon,
          },
          lateCheckinMinutes: response.data.data.late_checkin_minutes ?? 0,
          earlyLeaveMinutes: response.data.data.early_leave_minutes ?? 0,
          checkinWindowViolationMinutes:
            response.data.data.checkin_window_violation_minutes ?? 0,
          overtimeMinutes: response.data.data.overtime_minutes ?? 0,
          isSpecialShift: response.data.data.is_special_shift === true,
        }));

        resetLocationSelection();
        fetchHistory(currentPage);
        setIsCheckOutProcessing(false);
        showPopup('Success', 'Successfully checked out!');
        clearFormFields();
      } catch (error) {
        console.error('Checkout error:', error);
        setIsCheckOutProcessing(false);
        if (error.response?.data?.detail) {
          showPopup('Error', `Failed to check out: ${error.response.data.detail}`);
        } else {
          showPopup('Error', 'Failed to check out. Please try again later.');
        }
      }
    } catch (error) {
      console.error('Error processing check-out:', error);
      showPopup('Error', 'Error processing check-out. Please try again.');
      setIsCheckOutProcessing(false);
    }
  };

  const CheckInCheckOutIcon = () => (
    <svg
      width="1.5em"
      height="1.5em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="7" r="2" />
      <path d="M10 11h4c1.1 0 2 .9 2 2v3h-8v-3c0-1.1.9-2 2-2z" />
      <path d="M16 7h6" />
      <path d="M22 7l-3-3" />
      <path d="M22 7l-3 3" />
      <path d="M8 17H2" />
      <path d="M2 17l3-3" />
      <path d="M2 17l3 3" />
    </svg>
  );

  useEffect(() => {
    if (status === 'checkedIn' && checkInInfo?.time && !checkInInfo?.isStaleOpenSession) {
      const interval = setInterval(() => {
        const now = new Date();
        const [hours, minutes] = checkInInfo.time.split(':').map(Number);
        const checkInDateTime = new Date(checkInInfo.date);
        checkInDateTime.setHours(hours);
        checkInDateTime.setMinutes(minutes);
        const diffMs = now - checkInDateTime;

        const diffMinutes = Math.floor(diffMs / 60000);
        const hrs = Math.floor(diffMinutes / 60);
        const mins = diffMinutes % 60;

        const formatted = `${hrs > 0 ? `${hrs} hr${hrs > 1 ? 's' : ''} ` : ''}${mins} min${
          mins !== 1 ? 's' : ''
        }`;

        setCheckInInfo((prev) => ({
          ...prev,
          durationText: formatted,
        }));
      }, 60 * 1000);

      return () => clearInterval(interval);
    }
  }, [status, checkInInfo?.time]);

  return (
    <div className="p-4 sm:p-5 max-w-full w-full min-w-0">
      <div className="section-header !md:flex-row !flex-row !items-center bg-[#674936] text-white p-4 rounded">
        <h3 className="flex gap-4 text-white text-[1rem] md:text-[1.6rem] font-bold">
          <CheckInCheckOutIcon /> Check In & Check Out
        </h3>
        <div className="flex gap-2">
          {status !== 'checkedIn' ? (
            <button
              onClick={handleCheckIn}
              disabled={isCheckInProcessing}
              className={`bg-white text-[#674936] border-none sm:px-6 px-2 py-3 rounded-lg cursor-pointer text-sm transition-colors duration-300 font-semibold font-['Titillium_Web'] hover:bg-[#e2dbd6] ${isCheckInProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isCheckInProcessing ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#674936]"></div>
                  Processing...
                </div>
              ) : (
                'Check In'
              )}
            </button>
          ) : (
            <>
              <button
                onClick={handleCheckIn}
                disabled={isCheckInProcessing}
                className={`bg-white text-[#674936] border-none sm:px-6 px-2 py-3 rounded-lg cursor-pointer text-sm transition-colors duration-300 font-semibold font-['Titillium_Web'] hover:bg-[#e2dbd6] ${isCheckInProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isCheckInProcessing ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#674936]"></div>
                    Processing...
                  </div>
                ) : (
                  'Check In'
                )}
              </button>
              <button
                onClick={handleCheckOut}
                disabled={isCheckOutProcessing}
                className={`bg-white text-[#674936] border-none sm:px-6 px-2 py-3 rounded-lg cursor-pointer text-sm transition-colors duration-300 font-semibold font-['Titillium_Web'] hover:bg-[#e2dbd6] ${isCheckOutProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isCheckOutProcessing ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#674936]"></div>
                    Processing...
                  </div>
                ) : (
                  'Check Out'
                )}
              </button>
            </>
          )}
        </div>
      </div>



      {/* Location permission prompt */}
      {!locationPermissionGranted && (
        <div className="w-full min-w-0 bg-[#fff7f5] border border-[#f5c6cb] rounded-md p-3 my-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-red-700">
              <strong>Use your location?</strong>
              <div className="opacity-80">Enable location to auto-detect the nearest site.</div>
              {permissionState === 'denied' && (
                <div className="opacity-80 mt-1">Location access is blocked in your browser. Allow it in site settings.</div>
              )}
            </div>
            <button
              onClick={requestLocationPermission}
              className="bg-[#674936] text-white px-4 py-2 rounded-md hover:bg-[#503220] whitespace-nowrap"
            >
              Enable Location
            </button>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '20px' }} className="w-full min-w-0 max-w-xl">
        <label htmlFor="location">Select Location:</label>
        {isDetectingLocation && (
          <span className="ml-2 text-sm text-gray-500">Detecting location…</span>
        )}
        <br />
        <select
          id="location"
          value={selectedLocation}
          onChange={(e) => {
            setSelectedLocation(e.target.value);
            setLocationAutoMatchFailed(false);
            // Track if user manually unselected
            if (e.target.value === '') {
              setHasManuallyUnselected(true);
            } else {
              setHasManuallyUnselected(false);
            }
          }}
          style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          disabled={false}
        >
          <option value="">-- Select a location --</option>
          {locations.map((loc, idx) => {
            const isMissingCoordinates = loc.latitude == null || loc.longitude == null;
            
            // Check if location is within configured radius of user's current position
            let isNearby = false;
            if (!isMissingCoordinates && userCoordinates && locationPermissionGranted) {
              const distance = calculateDistance(
                userCoordinates.latitude,
                userCoordinates.longitude,
                loc.latitude,
                loc.longitude
              );
              isNearby = distance <= LOCATION_RADIUS_METERS;
            }

            return (
              <option key={idx} value={loc.name} disabled={isMissingCoordinates}>
                {loc.name}{isNearby ? ' (near you)' : ''}
              </option>
            );
          })}
        </select>
        {locationAutoMatchFailed && !selectedLocation && (
          <div className="text-[#8a6d3b] bg-[#fffbea] border border-[#fce588] rounded-md p-2 mt-2 text-sm">
            We couldn't automatically detect your location.
          </div>
        )}
      </div>

      {(status === 'checkedIn' || status === 'checkedOut') && checkInInfo && (
        <div
          style={{
            border: '1px solid #ccc',
            borderRadius: '8px',
            padding: '15px',
            backgroundColor: '#f9f9f9',
          }}
          className="w-full min-w-0 max-w-3xl mb-8"
        >
          <h3 className="flex items-center">
            <span className="font-bold text-lg">Status:</span>
            <span
              className={`ml-auto inline-block px-4 py-1 rounded-full font-semibold text-sm select-none ${
                status === 'checkedIn' ? 'bg-green-400 text-white' : 'bg-red-400 text-white'
              }`}
            >
              {status === 'checkedIn' ? 'Checked In' : 'Checked Out'}
            </span>
          </h3>
          <p>
            <strong>Location:</strong> {checkInInfo.location}
          </p>
          <p>
            <strong>Date:</strong> {checkInInfo.date}
          </p>
          <p>
            <strong>Check In Time:</strong> {checkInInfo.time}
          </p>
          <p>
            <strong>Duration:</strong> {checkInInfo.durationText ?? '0h 0m'}
          </p>
          {status === 'checkedOut' && checkInInfo.checkOutTime && (
            <p>
              <strong>Check Out Time:</strong> {checkInInfo.checkOutTime}
            </p>
          )}
          <p>
            <strong>Late Check-In:</strong>{' '}
            {formatAttendanceMinutes(
              checkInInfo.lateCheckinMinutes,
              checkInInfo.isSpecialShift,
              'late'
            )}
          </p>
          <p>
            <strong>Early Leave:</strong>{' '}
            {formatAttendanceMinutes(
              checkInInfo.earlyLeaveMinutes,
              checkInInfo.isSpecialShift,
              'early'
            )}
          </p>
          <p>
            <strong>Check-in Window Violation:</strong>{' '}
            {formatAttendanceMinutes(
              checkInInfo.checkinWindowViolationMinutes,
              checkInInfo.isSpecialShift
            )}
          </p>
          <p>
            <strong>Overtime:</strong>{' '}
            {formatAttendanceMinutes(checkInInfo.overtimeMinutes, checkInInfo.isSpecialShift)}
          </p>
        </div>
      )}

      <div className="flex flex-col gap-3 my-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-bold text-[18px]">Previous Records</h2>
          <div className="flex flex-col items-end gap-1">
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting || loading}
              className="bg-[#674936] text-white px-4 py-2 rounded-lg hover:bg-[#503220] transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? 'Exporting…' : 'Export to Excel'}
            </button>
            {exportError && (
              <p className="text-sm text-red-600">{exportError}</p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <AttendanceDaysFilter
            id="checkin-history-days"
            compact
            mode={attendanceFilterMode}
            days={attendanceDays}
            onModeChange={handleAttendanceFilterModeChange}
            onDaysChange={(value) => {
              setAttendanceDays(value);
              setCurrentPage(1);
            }}
          />
          {attendanceFilterMode === ATTENDANCE_DAYS_FILTER_CUSTOM && (
            <AttendanceDateRangeFilter
              idPrefix="checkin-history"
              compact
              startDate={startDate}
              endDate={endDate}
              onStartChange={handleAttendanceStartChange}
              onEndChange={handleAttendanceEndChange}
            />
          )}
          <p className="text-sm text-gray-600 pb-2 w-full sm:w-auto">
            {formatAttendanceHistorySummary(
              attendanceDays,
              attendanceFilterMode === ATTENDANCE_DAYS_FILTER_CUSTOM ? startDate : '',
              attendanceFilterMode === ATTENDANCE_DAYS_FILTER_CUSTOM ? endDate : '',
              formatUsDateFromIso
            )}
          </p>
        </div>
      </div>

      <div className="relative overflow-x-auto border border-gray-200 rounded-lg w-full shadow-sm mx-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm text-left">
          <thead className="bg-[#674936]">
            <tr>
              <th className="px-4 py-3 text-white font-semibold">Location</th>
              <th className="px-4 py-3 text-white font-semibold">Check In - Check Out</th>
              <th className="px-4 py-3 text-white font-semibold">Total Work Time</th>
              <th className="px-4 py-3 text-white font-semibold">Date</th>
              <th className="px-4 py-3 text-white font-semibold">Shift</th>
              <th className="px-4 py-3 text-white font-semibold">Min. Work</th>
              <th className="px-4 py-3 text-white font-semibold">Break</th>
              <th className="px-4 py-3 text-white font-semibold">Late In</th>
              <th className="px-4 py-3 text-white font-semibold">Early Leave</th>
              <th className="px-4 py-3 text-white font-semibold">Overtime</th>
              <th className="px-4 py-3 text-white font-semibold">Manager Informed</th>
              <th className="px-4 py-3 text-white font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={12} className="text-center py-8 text-gray-500">
                  <Loader />
                </td>
              </tr>
            ) : history.length === 0 ? (
              <tr>
                <td colSpan={12} className="text-center py-8 text-gray-500">
                  No records found.
                </td>
              </tr>
            ) : (
              history.map((record) => {
                const hasDetails =
                  record.image_url ||
                  record.image_url_checkout ||
                  record.check_in_no_location_reason ||
                  record.check_out_no_location_reason;
                return (
                <tr 
                  key={record.id} 
                  className={`bg-white hover:bg-gray-50${hasDetails ? ' cursor-pointer' : ''}`}
                  onClick={() => {
                    if (hasDetails) {
                      openImageModal(record);
                    }
                  }}
                >
                  <td className="px-4 py-3">
                    {record.location_name}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {formatCheckInOutDurationFromRecord(record)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {record.day_total_hours_formatted || '-'}
                  </td>
                  <td className="px-4 py-3">{formatUsDateFromIso(record.work_date) || record.work_date || '-'}</td>
                  <td className="px-4 py-3">
                    <AttendanceShiftName
                      name={record.effective_shift_name}
                      isSpecialShift={record.is_special_shift}
                    />
                  </td>
                  <td className="px-4 py-3">
                    {formatShiftPolicyMinutes(record.min_work_minutes)}
                  </td>
                  <td className="px-4 py-3">
                    {formatShiftPolicyMinutes(record.break_duration_minutes)}
                  </td>
                  <td className="px-4 py-3">
                    {formatAttendanceMinutes(
                      record.late_checkin_minutes,
                      record.is_special_shift,
                      'late'
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {formatAttendanceMinutes(
                      record.early_leave_minutes,
                      record.is_special_shift,
                      'early'
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {formatAttendanceMinutes(record.overtime_minutes, record.is_special_shift)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        record.check_in_inform_manager || record.check_out_inform_manager
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      <span className="w-2 h-2 mr-1 rounded-full bg-current"></span>
                      {record.check_in_inform_manager || record.check_out_inform_manager ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusClass(
                        record.status
                      )}`}
                    >
                      <span className="w-2 h-2 mr-1 rounded-full bg-current"></span>
                      {getStatusLabel(record.status)}
                    </span>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>

        <div className="p-4 flex justify-center">
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(newPage) => setCurrentPage(newPage)}
          />
        </div>
      </div>

      <SelfieCapture
        isOpen={showSelfieCapture}
        onClose={() => {
          closeSelfieCapture();
          setNoLocationReason('');
          setNoLocationInformManager(false);
          setNoLocationSelectedLocation('');
          setManualLatitude('');
          setManualLongitude('');
          setCurrentAction(null);
          setIsCheckInProcessing(false);
          setIsCheckOutProcessing(false);
        }}
        onCapture={handleSelfieCapture}
        title="Take Selfie"
        buttonText={currentAction === 'checkIn' ? 'Check In' : 'Check Out'}
        showLoading={isProcessing}
        loadingText={currentAction === 'checkIn' ? 'Checking you in...' : 'Checking you out...'}
        onBeforeCapture={() => {
          if (isManualAttendanceFlow()) {
            if (currentAction === 'checkIn') {
              return validateManualCheckInFields();
            }
            if (currentAction === 'checkOut') {
              return validateManualCheckOutFields();
            }
          } else if (currentAction === 'checkOut' && needsEarlyCheckoutAcknowledgment()) {
            return validateEarlyCheckOutFields();
          }
          return true;
        }}
        extraContent={
          isManualAttendanceFlow() ? (
          <div className="mb-4 text-left">
            <div className="form-field">
              <label className="block">Location *</label>
              <input
                type="text"
                placeholder="Enter your location/site name"
                value={noLocationSelectedLocation}
                onChange={(e) => setNoLocationSelectedLocation(e.target.value)}
                className="w-full border rounded focus:outline-none focus:border-gray-500"
                required
              />
            </div>
            <div className="form-row mt-2">
              <div className="form-field">
                <label className="block">Latitude *</label>
                <input
                  type="text"
                  placeholder="Enter latitude"
                  value={userCoordinates ? userCoordinates.latitude.toFixed(6) : manualLatitude}
                  onChange={(e) => setManualLatitude(e.target.value)}
                  disabled={!!userCoordinates}
                  className="w-full border rounded focus:outline-none focus:border-gray-500"
                  required
                />
              </div>
              <div className="form-field">
                <label className="block">Longitude *</label>
                <input
                  type="text"
                  placeholder="Enter longitude"
                  value={userCoordinates ? userCoordinates.longitude.toFixed(6) : manualLongitude}
                  onChange={(e) => setManualLongitude(e.target.value)}
                  disabled={!!userCoordinates}
                  className="w-full border rounded focus:outline-none focus:border-gray-500"
                  required
                />
              </div>
            </div>
            <div className="form-field mt-2">
              <label className="block">Reason *</label>
              <textarea
                placeholder="Explain why auto-location did not work"
                value={noLocationReason}
                onChange={(e) => setNoLocationReason(e.target.value)}
                className="w-full border rounded focus:outline-none focus:border-gray-500"
                required
              />
            </div>
            <div className="form-field mt-2">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={noLocationInformManager}
                  onChange={(e) => setNoLocationInformManager(e.target.checked)}
                  aria-required="true"
                />
                Inform my manager *
              </label>
            </div>
          </div>
          ) : currentAction === 'checkOut' && needsEarlyCheckoutAcknowledgment() ? (
          <div className="mb-4 text-left">
            <p className="text-sm text-gray-600 mb-2">
              You are checking out before the required work duration.
            </p>
            <div className="form-field mt-2">
              <label className="block">Reason *</label>
              <textarea
                placeholder="Explain why you are leaving early"
                value={noLocationReason}
                onChange={(e) => setNoLocationReason(e.target.value)}
                className="w-full border rounded focus:outline-none focus:border-gray-500"
                required
              />
            </div>
            <div className="form-field mt-2">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={noLocationInformManager}
                  onChange={(e) => setNoLocationInformManager(e.target.checked)}
                  aria-required="true"
                />
                Inform my manager *
              </label>
            </div>
          </div>
          ) : null
        }
      />

      <AttendancePopup
        title={popupTitle}
        message={popupMessage}
        isOpen={isPopupOpen}
        onClose={closePopup}
        secondaryButtonText={popupSecondaryButtonText}
        onSecondaryClick={popupOnSecondaryClick}
      />

      {/* Nearby Locations Selection Modal */}
      {showNearbyLocationsModal && nearbyLocations.length > 0 && (
        <div 
          className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 overflow-y-auto overscroll-contain py-4 px-2 sm:items-center sm:py-6"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowNearbyLocationsModal(false);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 max-w-md w-full mx-4 max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto overscroll-contain min-w-0 my-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-800">
                Multiple Nearby Locations Found
              </h2>
              <button
                onClick={() => setShowNearbyLocationsModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              We found {nearbyLocations.length} location{nearbyLocations.length > 1 ? 's' : ''} within {LOCATION_RADIUS_METERS} meters of your current position. Please select your location:
            </p>
            <div className="mb-4 max-h-64 overflow-y-auto">
              {nearbyLocations.map((location, index) => (
                <div
                  key={index}
                  onClick={() => {
                    setSelectedLocation(location.name);
                    setShowNearbyLocationsModal(false);
                    setHasManuallyUnselected(false);
                  }}
                  className={`p-3 mb-2 border rounded-lg cursor-pointer transition-colors ${
                    selectedLocation === location.name
                      ? 'bg-[#674936] text-white border-[#674936]'
                      : 'bg-white border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">{location.name}</span>
                    <span className={`text-sm ${selectedLocation === location.name ? 'text-white' : 'text-gray-600'}`}>
                      {location.distance}m away
                    </span>
                  </div>
                  {selectedLocation === location.name && (
                    <div className="text-sm mt-1 opacity-90">✓ Selected</div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 justify-end">
              <button
                onClick={() => {
                  setSelectedLocation('');
                  setShowNearbyLocationsModal(false);
                  setHasManuallyUnselected(true);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-gray-700"
              >
                Deselect & Use Manual Entry
              </button>
              <button
                onClick={() => {
                  setShowNearbyLocationsModal(false);
                  setHasManuallyUnselected(false);
                }}
                className="px-4 py-2 bg-[#674936] text-white rounded-lg hover:bg-[#503220] transition-colors font-semibold"
              >
                Confirm Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {showImageModal && selectedRecord && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 overflow-y-auto overscroll-contain py-4 px-2 sm:items-center sm:py-6">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 max-w-4xl w-full mx-4 max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto overscroll-contain min-w-0 my-auto">
            <div className="flex justify-between items-start gap-3 mb-6 min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 break-words pr-2">Attendance Details</h2>
              <button
                onClick={closeImageModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            {/* Record Details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check In Location</h3>
                  <p className="text-gray-900">
                    {selectedRecord.check_in_location_name ||
                      selectedRecord.location_name ||
                      '-'}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check Out Location</h3>
                  <p className="text-gray-900">{selectedRecord.check_out_location_name || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check In Date</h3>
                  <p className="text-gray-900">
                    {formatUsDateFromIso(
                      selectedRecord.check_in_date || selectedRecord.work_date
                    ) ||
                      selectedRecord.check_in_date ||
                      selectedRecord.work_date ||
                      '-'}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check Out Date</h3>
                  <p className="text-gray-900">
                    {formatUsDateFromIso(selectedRecord.check_out_date) ||
                      selectedRecord.check_out_date ||
                      '-'}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check In Time</h3>
                  <p className="text-gray-900">{selectedRecord.check_in_time_str || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check Out Time</h3>
                  <p className="text-gray-900">{selectedRecord.check_out_time_str || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Duration</h3>
                  <p className="text-gray-900">{selectedRecord.total_hours_formatted || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Total Work Time (Day)</h3>
                  <p className="text-gray-900">{selectedRecord.day_total_hours_formatted || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Manager Informed</h3>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      selectedRecord.check_in_inform_manager || selectedRecord.check_out_inform_manager
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    <span className="w-2 h-2 mr-1 rounded-full bg-current"></span>
                    {selectedRecord.check_in_inform_manager || selectedRecord.check_out_inform_manager ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="md:col-span-2">
                  <h3 className="font-semibold text-gray-700 mb-2">Check In Reason</h3>
                  <p className="text-gray-900">{selectedRecord.check_in_no_location_reason || '-'}</p>
                </div>
                <div className="md:col-span-2">
                  <h3 className="font-semibold text-gray-700 mb-2">Check Out Reason</h3>
                  <p className="text-gray-900">{selectedRecord.check_out_no_location_reason || '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check-in Latitude</h3>
                  <p className="text-gray-900">{selectedRecord.check_in_lat ? (selectedRecord.check_in_lat % 1 === 0 ? selectedRecord.check_in_lat.toString() : parseFloat(selectedRecord.check_in_lat).toFixed(6).replace(/\.?0+$/, '')) : '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check-in Longitude</h3>
                  <p className="text-gray-900">{selectedRecord.check_in_lon ? (selectedRecord.check_in_lon % 1 === 0 ? selectedRecord.check_in_lon.toString() : parseFloat(selectedRecord.check_in_lon).toFixed(6).replace(/\.?0+$/, '')) : '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check-out Latitude</h3>
                  <p className="text-gray-900">{selectedRecord.check_out_lat ? (selectedRecord.check_out_lat % 1 === 0 ? selectedRecord.check_out_lat.toString() : parseFloat(selectedRecord.check_out_lat).toFixed(6).replace(/\.?0+$/, '')) : '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Check-out Longitude</h3>
                  <p className="text-gray-900">{selectedRecord.check_out_lon ? (selectedRecord.check_out_lon % 1 === 0 ? selectedRecord.check_out_lon.toString() : parseFloat(selectedRecord.check_out_lon).toFixed(6).replace(/\.?0+$/, '')) : '-'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Status</h3>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusClass(
                      selectedRecord.status
                    )}`}
                  >
                    <span className="w-2 h-2 mr-1 rounded-full bg-current"></span>
                    {getStatusLabel(selectedRecord.status)}
                  </span>
                </div>
              </div>
            </div>

            {/* Images Section */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-700 mb-4">Selfie Images</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Check In Image */}
                {selectedRecord.image_url && (
                  <div className="text-center">
                    <h4 className="font-medium text-gray-600 mb-2">Check In Selfie</h4>
                    <div className="relative">
                      <img
                        src={selectedRecord.image_url}
                        alt="Check in selfie"
                        className="w-full h-48 object-cover rounded-lg shadow-lg mx-auto"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div 
                        className="hidden items-center justify-center h-48 bg-gray-100 rounded-lg text-gray-500"
                        style={{ display: 'none' }}
                      >
                        <div className="text-center">
                          <div className="text-4xl mb-2">📷</div>
                          <p>Image could not be loaded</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Check Out Image */}
                {selectedRecord.image_url_checkout && (
                  <div className="text-center">
                    <h4 className="font-medium text-gray-600 mb-2">Check Out Selfie</h4>
                    <div className="relative">
                      <img
                        src={selectedRecord.image_url_checkout}
                        alt="Check out selfie"
                        className="w-full h-48 object-cover rounded-lg shadow-lg mx-auto"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div 
                        className="hidden items-center justify-center h-48 bg-gray-100 rounded-lg text-gray-500"
                        style={{ display: 'none' }}
                      >
                        <div className="text-center">
                          <div className="text-4xl mb-2">📷</div>
                          <p>Image could not be loaded</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* No Images Message */}
                {!selectedRecord.image_url && !selectedRecord.image_url_checkout && (
                  <div className="col-span-2 text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">📷</div>
                    <p>No selfie images available for this record</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-center">
              <button
                onClick={closeImageModal}
                className="bg-[#674936] text-white px-6 py-2 rounded-lg hover:bg-[#503220] transition-colors duration-300 font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}